import { useEffect, useRef } from 'react';

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const monogramRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const taglineRef = useRef<HTMLParagraphElement>(null);
  const subtitleRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    const mg = setTimeout(() => {
      monogramRef.current?.classList.remove('opacity-0', 'translate-y-6', 'scale-95');
      monogramRef.current?.classList.add('opacity-100', 'translate-y-0', 'scale-100');
    }, 200);
    timers.push(mg);

    const tt = setTimeout(() => {
      titleRef.current?.classList.remove('opacity-0', 'translate-y-6');
      titleRef.current?.classList.add('opacity-100', 'translate-y-0');
    }, 600);
    timers.push(tt);

    const tg = setTimeout(() => {
      taglineRef.current?.classList.remove('opacity-0', 'translate-y-3');
      taglineRef.current?.classList.add('opacity-100', 'translate-y-0');
    }, 1000);
    timers.push(tg);

    const st = setTimeout(() => {
      subtitleRef.current?.classList.remove('opacity-0', 'translate-y-4');
      subtitleRef.current?.classList.add('opacity-100', 'translate-y-0');
    }, 1400);
    timers.push(st);

    const bt = setTimeout(() => {
      btnRef.current?.classList.remove('opacity-0', 'translate-y-4');
      btnRef.current?.classList.add('opacity-100', 'translate-y-0');
    }, 1700);
    timers.push(bt);

    return () => timers.forEach(clearTimeout);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Soft radial glow behind content */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[800px] md:h-[800px] rounded-full bg-brand/5 blur-[120px]" />
      </div>

      {/* Content — perfectly centered */}
      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 md:px-10 py-32 md:py-40 flex flex-col items-center text-center">
        {/* Monogram MD */}
        <div
          ref={monogramRef}
          className="mb-12 md:mb-16 opacity-0 translate-y-6 scale-95 transition-all duration-[1100ms] ease-out"
        >
          <img
            src="https://public.readdy.ai/ai/img_res/7da60a38-055d-4b6c-a565-230d6c7a9e37.png"
            alt="MD Monogram"
            className="h-28 sm:h-36 md:h-44 w-auto object-contain"
          />
        </div>

        {/* Tagline */}
        <p
          ref={taglineRef}
          className="mt-4 md:mt-5 text-sm md:text-base font-light tracking-[0.15em] uppercase text-white/40 opacity-0 translate-y-3 transition-all duration-700"
        >
          Basée à Montreux &bull; Projets sans frontières
        </p>

        {/* Decorative line */}
        <div className="mt-8 md:mt-10 w-16 h-px bg-brand/40" />

        {/* Subtitle */}
        <div
          ref={subtitleRef}
          className="mt-8 md:mt-10 flex flex-col items-center gap-2 md:gap-3 opacity-0 translate-y-4 transition-all duration-700"
        >
          <p className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-medium text-white tracking-[0.08em] uppercase leading-snug">
            Une présence qui{' '}
            <span className="text-brand">attire</span>.
          </p>
          <p className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-medium text-white tracking-[0.08em] uppercase leading-snug">
            Une image qui{' '}
            <span className="text-brand">marque</span>.
          </p>
        </div>

        {/* CTA */}
        <div
          ref={btnRef}
          className="mt-14 md:mt-20 opacity-0 translate-y-4 transition-all duration-700"
        >
          <button
            onClick={() => scrollTo('contact')}
            className="btn-primary px-10 py-4 text-base whitespace-nowrap tracking-wide"
          >
            Discuter de mon projet
          </button>
        </div>
      </div>
    </section>
  );
}
